import {
  AlarmClock,
  CalendarRange,
  ClipboardList,
  Download,
  Info,
  PencilLine,
  Timer,
} from 'lucide-react';
import { useState } from 'react';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { KpiCard } from '@/components/ui/kpi-card';
import { useEmployees } from '@/hooks/employees';
import { timesheetExportUrl, useTimesheet, type TimesheetParams } from '@/hooks/timesheets';
import { formatDay, formatInstant } from '@/lib/dates';
import {
  CorrectShiftDialog,
  type CorrectableShift,
} from '@/pages/admin/timesheets/CorrectShiftDialog';
import { formatMinutes, type OvertimeRule } from '@shared/overtime';
import {
  TIMESHEET_PERIOD_LABELS,
  type EmployeeTimesheet,
  type TimesheetPeriod,
  type TimesheetResponse,
} from '@shared/schemas/timesheet';

/*
 * /admin/timesheets — ROADMAP.md §10.2, business-rules.md §5, defect P13.
 *
 * **The rule that produced these numbers is named on the screen**, in a line
 * that cannot be missed. business-rules.md §5 asks for it by name, and the
 * reason is P13: the prototype's timesheet computes overtime at 8 hours a day
 * and says nothing about it, so a wrong figure looks exactly like a right one.
 * Somebody reading "Over 40 hours in a week" can say that is not what we agreed;
 * nobody can say that about a bare number.
 *
 * **Hours are hours and minutes, never a decimal.** `41h 15m`, not `41.25` —
 * which invites a spreadsheet to multiply it by a rate, and invites a float
 * while doing it. `formatMinutes` renders at the display edge, the same rule
 * `formatCents` follows for money.
 *
 * **There is no pay figure on this screen and that is deliberate.** The
 * deliverable is the right hours split correctly; what they are worth needs an
 * overtime multiplier, and that is the owner's accountant's decision rather than
 * one to make in a React component.
 *
 * **Weeks are shown whole.** A range that starts mid-week is widened outward,
 * because overtime is a property of a workweek and a threshold applied to three
 * days of one is a number that means nothing. The header says so when it
 * happens, rather than quietly answering a different question.
 */

const PERIODS: { value: TimesheetPeriod; label: string }[] = [
  { value: 'this_week', label: TIMESHEET_PERIOD_LABELS.this_week },
  { value: 'last_week', label: TIMESHEET_PERIOD_LABELS.last_week },
  { value: 'this_month', label: TIMESHEET_PERIOD_LABELS.this_month },
  { value: 'last_month', label: TIMESHEET_PERIOD_LABELS.last_month },
];

/**
 * The sentence under the rule's name — **one per rule, keyed off the live
 * setting**.
 *
 * This was a single fixed paragraph reading "Utah follows the federal rule —
 * there is no daily trigger. Four ten-hour days is forty hours and no overtime",
 * and it was **wrong the moment anybody changed the setting**. Found by changing
 * it and looking at the screen: the heading said "Over 8 hours in a day", the
 * table underneath showed eight hours of overtime on four ten-hour days, and the
 * paragraph between them said there was no daily trigger and that those days
 * were not overtime.
 *
 * That is the same defect this slice exists to fix, one layer up. P13 is a
 * timesheet whose number comes from a rule nobody stated; this was a timesheet
 * that stated a rule its number did not come from — and the prose is more
 * believable than the figure, so it is arguably worse.
 *
 * The rule comes from the server. So does the explanation of it, now.
 */
const RULE_EXPLANATIONS: Record<OvertimeRule, string> = {
  weekly_over_40:
    'Utah follows the federal rule — hours over 40 in a workweek, with no daily trigger. Four ten-hour days is forty hours and no overtime.',
  daily_over_8:
    'Hours over 8 in a single day, with no weekly threshold. This is not Utah’s rule — four ten-hour days counts eight hours of overtime here.',
  both: 'Hours over 8 in a day, and over 40 in a week on top of that. Each hour is counted once. This is not Utah’s rule.',
};

/** True on the whole of both, and the only one Utah's answer applies to. */
const BREAK_NOTE =
  'Unpaid meal breaks of thirty minutes or more come out before the threshold; shorter rest breaks are paid time and are not deducted.';

export function TimesheetsPage() {
  const [period, setPeriod] = useState<TimesheetPeriod>('this_week');
  const [employeeId, setEmployeeId] = useState('');
  const [correcting, setCorrecting] = useState<CorrectableShift | null>(null);

  const timesheet = useTimesheet({
    period,
    ...(employeeId === '' ? {} : { employeeId }),
  });

  /*
   * The unsearched list is capped at `MAX_PAGE_SIZE`, which `JobCreateDialog`
   * calls "a ceiling rather than a promise". For a business with eight cleaners
   * that ceiling is nowhere near, and this filter is a convenience rather than
   * the only way to reach a person — the employee list is.
   */
  const people = useEmployees({
    sort: 'name',
    page: 1,
    activity: 'active',
    search: '',
    positionId: '',
    skillId: '',
  });

  const data = timesheet.data;
  const totals = data?.totals;

  const exportParams: TimesheetParams = {
    period,
    ...(employeeId === '' ? {} : { employeeId }),
  };

  return (
    <AdminPage
      title="Timesheets"
      description="Hours clocked, split into regular and overtime."
      actions={
        <Button variant="outline" asChild>
          {/*
            **`download` is what stops a CSV rendering as text in a tab** — the
            same note the Reports page carries. Disabled until the period has
            loaded, because exporting a window the screen has not resolved yet
            would produce a file for a range nobody chose.
          */}
          <a href={timesheetExportUrl(exportParams)} download>
            <Download aria-hidden="true" />
            Export CSV
          </a>
        </Button>
      }
    >
      {/*
        **The rule, named.** business-rules.md §5. This is the difference
        between a number somebody can check and one they have to trust.
      */}
      <div className="mb-6 flex items-start gap-3 rounded-lg border border-ink-300 bg-honey-50 p-4">
        <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-bark-700" />
        <div>
          <p className="text-label text-ink-900">
            Overtime rule: {data ? data.ruleLabel : 'Loading…'}
          </p>
          <p className="mt-0.5 text-meta text-ink-500">
            {/*
              Both halves come from the rule the server actually used. A fixed
              sentence here contradicted the figures beside it the moment the
              setting changed — see RULE_EXPLANATIONS.
            */}
            {data ? `${RULE_EXPLANATIONS[data.rule]} ${BREAK_NOTE}` : 'Reading the payroll rule…'}
          </p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <FilterPills
          label="Choose a period"
          options={PERIODS}
          value={period}
          onChange={setPeriod}
        />

        <label className="ml-auto flex items-center gap-2 text-body text-ink-500">
          <span>Employee</span>
          <select
            className="min-h-control rounded-sm border border-ink-300 bg-white px-3 text-body text-ink-900"
            value={employeeId}
            onChange={(event) => {
              setEmployeeId(event.target.value);
            }}
          >
            <option value="">Everyone</option>
            {(people.data?.data ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {person.fullName}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/*
        Four figures, always numbers. A zero is `0` — P1, P10 — and `isLoading`
        shows a skeleton rather than a dash.
      */}
      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Hours clocked"
          value={totals?.clockedMinutes ?? null}
          format={formatMinutes}
          isLoading={timesheet.isPending}
          icon={Timer}
        />
        <KpiCard
          label="Regular"
          value={totals?.regularMinutes ?? null}
          format={formatMinutes}
          isLoading={timesheet.isPending}
          icon={ClipboardList}
        />
        <KpiCard
          label="Overtime"
          value={totals?.overtimeMinutes ?? null}
          format={formatMinutes}
          isLoading={timesheet.isPending}
          icon={AlarmClock}
        />
        <KpiCard
          label="Shifts"
          value={totals?.shiftCount ?? null}
          isLoading={timesheet.isPending}
          icon={CalendarRange}
        />
      </div>

      {data ? <RangeNote data={data} /> : null}

      {timesheet.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          {/* A failed request is an error, never an empty state — P22. */}
          <ErrorState
            onRetry={() => {
              void timesheet.refetch();
            }}
          />
        </div>
      ) : timesheet.isPending ? (
        <div aria-busy="true">
          <span className="sr-only">Loading timesheets</span>
          <div aria-hidden="true" className="h-48 animate-pulse rounded-lg bg-ink-100" />
        </div>
      ) : data && data.employees.length > 0 ? (
        <ul className="space-y-4">
          {data.employees.map((sheet) => (
            <EmployeeCard key={sheet.employeeId} sheet={sheet} onCorrect={setCorrecting} />
          ))}
        </ul>
      ) : (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={Timer}
            title="Nobody has clocked any hours in this period."
            hint="Cleaners clock in and out from the employee portal."
          />
        </div>
      )}

      <CorrectShiftDialog
        shift={correcting}
        onClose={() => {
          setCorrecting(null);
        }}
      />
    </AdminPage>
  );
}

/**
 * Says what the numbers actually cover when it is not what was asked for.
 *
 * Only rendered when the range was widened — the usual case is a whole week and
 * a permanent explanation of something that has not happened is noise.
 */
function RangeNote({ data }: { data: TimesheetResponse }) {
  const widened = data.from !== data.requestedFrom || data.to !== data.requestedTo;

  return (
    <p className="mb-4 text-meta text-ink-500">
      {formatDay(data.from)} to {formatDay(data.to)}
      {widened ? (
        <>
          {' '}
          — widened from {formatDay(data.requestedFrom)}–{formatDay(data.requestedTo)}, because
          overtime is worked out over a whole week.
        </>
      ) : null}
    </p>
  );
}

function EmployeeCard({
  sheet,
  onCorrect,
}: {
  sheet: EmployeeTimesheet;
  onCorrect: (shift: CorrectableShift) => void;
}) {
  const worked = sheet.shifts.length > 0;

  return (
    <li className="overflow-hidden rounded-lg border border-ink-300 bg-white shadow-card">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-ink-300 px-4 py-3">
        <h2 className="text-h3 text-ink-900">
          {sheet.employeeName}
          {sheet.isActive ? null : (
            <span className="ml-2 text-meta text-ink-500">(no longer active)</span>
          )}
        </h2>

        <p className="text-body tabular-nums text-ink-900">
          {formatMinutes(sheet.regularMinutes)} regular
          {sheet.overtimeMinutes > 0 ? (
            <span className="ml-2 font-medium text-bark-700">
              + {formatMinutes(sheet.overtimeMinutes)} overtime
            </span>
          ) : null}
        </p>
      </div>

      {/*
        **Said out loud rather than left to be discovered.** An open shift is in
        none of the totals above, because a shift with no end has no duration
        and counting the minutes so far would make a figure that changes between
        two refreshes.
      */}
      {/*
        **The forgotten clock-out, with the button that fixes it.** §10.3's main
        job: `worker/tasks.ts` alerts admins about any entry open more than
        sixteen hours and tells them to "correct it on their timesheet" — which
        was a link to a screen with nothing to click until this existed.
      */}
      {sheet.openShift ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-300 bg-honey-50 px-4 py-2">
          <p className="text-meta text-ink-900">
            On the clock since {formatInstant(sheet.openShift.clockInAt)}
            {sheet.openShift.hoursSoFar >= 16 ? (
              <span className="ml-1 font-medium">
                — {String(sheet.openShift.hoursSoFar)} hours ago. Almost certainly a forgotten
                clock-out.
              </span>
            ) : (
              <span className="ml-1">— not counted in the totals above yet.</span>
            )}
          </p>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onCorrect({
                id: sheet.openShift!.id,
                clockInAt: sheet.openShift!.clockInAt,
                clockOutAt: null,
                employeeName: sheet.employeeName,
              });
            }}
          >
            Close this shift
          </Button>
        </div>
      ) : null}

      {worked ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] text-body">
            <caption className="sr-only">
              {sheet.employeeName}&apos;s hours, one row per workweek
            </caption>
            <thead>
              <tr className="border-b border-ink-300 text-meta uppercase tracking-wide text-ink-500">
                <th scope="col" className="px-4 py-2 text-left font-medium">
                  Week
                </th>
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  Clocked
                </th>
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  Break
                </th>
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  Regular
                </th>
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  Overtime
                </th>
              </tr>
            </thead>
            <tbody>
              {sheet.weeks.map((week) => (
                <tr key={week.weekStart} className="border-b border-ink-100 last:border-0">
                  <th scope="row" className="px-4 py-2 text-left font-normal text-ink-900">
                    {formatDay(week.weekStart)} – {formatDay(week.weekEnd)}
                    <span className="block text-meta text-ink-500">
                      {week.days.length} {week.days.length === 1 ? 'day' : 'days'} worked
                    </span>
                  </th>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatMinutes(week.clockedMinutes)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-ink-500">
                    {/* Zero is `0h 00m`, never a dash — P1. */}
                    {formatMinutes(week.breakMinutes)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatMinutes(week.regularMinutes)}
                  </td>
                  <td
                    className={
                      week.overtimeMinutes > 0
                        ? 'px-4 py-2 text-right font-medium tabular-nums text-bark-700'
                        : 'px-4 py-2 text-right tabular-nums text-ink-500'
                    }
                  >
                    {formatMinutes(week.overtimeMinutes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="px-4 py-4 text-body text-ink-500">
          {/*
            A person with no hours is shown rather than dropped — an empty week
            is a fact the office wants, and hiding them would make somebody who
            worked nothing look like somebody who left.
          */}
          No hours clocked in this period.
        </p>
      )}

      {worked ? <ShiftList sheet={sheet} onCorrect={onCorrect} /> : null}
    </li>
  );
}

/**
 * The individual punches, behind a disclosure — §10.3.
 *
 * **Collapsed by default**, because the weeks above are what payroll runs on and
 * a fortnight of one cleaner is twenty rows. It opens when somebody has a
 * question about a specific day, which is exactly when they need the times, who
 * changed them and why.
 *
 * A real `<details>` rather than a `useState` toggle: it is keyboard-operable
 * and screen-reader-announced without anything being wired up, and the focus
 * sweep gets it for free.
 */
function ShiftList({
  sheet,
  onCorrect,
}: {
  sheet: EmployeeTimesheet;
  onCorrect: (shift: CorrectableShift) => void;
}) {
  const corrected = sheet.shifts.filter((shift) => shift.wasCorrected).length;

  return (
    <details className="border-t border-ink-300">
      <summary className="min-h-touch cursor-pointer px-4 py-3 text-body text-bark-700">
        {sheet.shifts.length} {sheet.shifts.length === 1 ? 'shift' : 'shifts'}
        {corrected > 0 ? (
          <span className="ml-1 text-ink-500">· {String(corrected)} corrected</span>
        ) : null}
      </summary>

      <ul className="divide-y divide-ink-100 border-t border-ink-100">
        {sheet.shifts.map((shift) => (
          <li key={shift.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
            <div>
              <p className="text-body text-ink-900">
                {formatDay(shift.date)} · {formatInstant(shift.clockInAt)} –{' '}
                {formatInstant(shift.clockOutAt)}
                <span className="ml-2 tabular-nums text-ink-500">
                  {formatMinutes(shift.minutes)}
                </span>
              </p>

              {shift.jobNumber ? (
                <p className="text-meta text-ink-500">Job {shift.jobNumber}</p>
              ) : null}

              {/*
                **The originals, shown rather than flagged.** ROADMAP.md §10.3
                asks for "original values remain visible" — a badge saying an
                entry was touched does not answer "why does this week say 41
                hours", and the answer is what the office is looking for.
              */}
              {shift.wasCorrected ? (
                <div className="mt-1 rounded-sm bg-honey-50 p-2 text-meta text-ink-900">
                  <p>
                    <span className="font-medium">Corrected</span>
                    {shift.correctedByName ? ` by ${shift.correctedByName}` : null} — the clock said{' '}
                    {shift.originalClockInAt ? formatInstant(shift.originalClockInAt) : 'unknown'}
                    {' – '}
                    {shift.originalClockOutAt
                      ? formatInstant(shift.originalClockOutAt)
                      : 'never clocked out'}
                  </p>
                  {shift.correctionNote ? <p className="mt-0.5">{shift.correctionNote}</p> : null}
                </div>
              ) : null}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onCorrect({
                  id: shift.id,
                  clockInAt: shift.clockInAt,
                  clockOutAt: shift.clockOutAt,
                  employeeName: sheet.employeeName,
                });
              }}
            >
              <PencilLine aria-hidden="true" className="size-4" />
              Correct
            </Button>
          </li>
        ))}
      </ul>
    </details>
  );
}
